import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2, XCircle, History as HistoryIcon, X, FileSpreadsheet, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { formatDateIt, formatDateTimeIt } from "@/lib/timeSlots";

function groupByDate(items) {
  const map = new Map();
  for (const it of items) {
    if (!map.has(it.date)) map.set(it.date, []);
    map.get(it.date).push(it);
  }
  return Array.from(map.entries());
}

export default function HistoryPanel() {
  const [items, setItems] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teacherId, setTeacherId] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    api.get(`/teachers`).then((r) => setTeachers(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (teacherId && teacherId !== "all") params.teacher_id = teacherId;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const { data } = await api.get(`/history`, { params });
      setItems(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [teacherId, from, to]);

  useEffect(() => { load(); }, [load]);

  const resetFilters = () => { setTeacherId("all"); setFrom(""); setTo(""); };
  const hasFilters = teacherId !== "all" || !!from || !!to;
  const [exporting, setExporting] = useState(false);

  const exportHistory = async (format) => {
    setExporting(true);
    try {
      const params = { format };
      if (teacherId !== "all") params.teacher_id = teacherId;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const res = await api.get(`/history/export`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = format === "pdf" ? "storico.pdf" : "storico.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting(false);
    }
  };

  const groups = groupByDate(items);

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Storico avvisi</h1>
        <p className="text-muted-foreground mt-1">Sostituzioni dei giorni passati: chi è stato avvisato, quando e con quale canale.</p>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-3 rounded-md border border-border bg-background p-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Docente</Label>
          <Select value={teacherId} onValueChange={setTeacherId}>
            <SelectTrigger data-testid="history-teacher-filter" className="w-[220px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i docenti</SelectItem>
              {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="history-from" className="text-xs">Dal</Label>
          <Input id="history-from" type="date" data-testid="history-from" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[160px]" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="history-to" className="text-xs">Al</Label>
          <Input id="history-to" type="date" data-testid="history-to" value={to} onChange={(e) => setTo(e.target.value)} className="w-[160px]" />
        </div>
        {hasFilters && (
          <Button variant="ghost" data-testid="history-reset" onClick={resetFilters} className="gap-1 h-10">
            <X className="h-4 w-4" /> Azzera
          </Button>
        )}
        <div className="ml-auto flex items-end gap-2">
          <Button variant="outline" data-testid="export-xlsx" onClick={() => exportHistory("xlsx")} disabled={exporting || items.length === 0} className="h-10 gap-2">
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </Button>
          <Button variant="outline" data-testid="export-pdf" onClick={() => exportHistory("pdf")} disabled={exporting || items.length === 0} className="h-10 gap-2">
            <FileText className="h-4 w-4" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="rounded-md border border-border bg-background px-4 py-10 text-center text-muted-foreground">Caricamento…</div>
      ) : groups.length === 0 ? (
        <div className="rounded-md border border-border bg-background px-4 py-12 text-center text-muted-foreground" data-testid="history-empty">
          <HistoryIcon className="mx-auto h-8 w-8 mb-2 opacity-50" />
          Nessuna sostituzione nei giorni passati.
        </div>
      ) : (
        <div className="space-y-8" data-testid="history-list">
          {groups.map(([date, rows]) => (
            <div key={date} data-testid={`history-day-${date}`}>
              <h2 className="font-heading text-lg font-bold mb-2 flex items-center gap-2">
                <span className="inline-flex h-2 w-2 rounded-full bg-primary" />
                {formatDateIt(date)}
                <span className="text-sm font-normal text-muted-foreground">({rows.length})</span>
              </h2>
              <div className="rounded-md border border-border bg-background overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-secondary/60 text-left">
                      <th className="px-4 py-2.5 font-semibold w-40">Ora</th>
                      <th className="px-4 py-2.5 font-semibold w-24">Classe</th>
                      <th className="px-4 py-2.5 font-semibold">Docente</th>
                      <th className="px-4 py-2.5 font-semibold w-72">Avviso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const ch = r.notified_channels || [];
                      return (
                        <tr key={r.id} data-testid={`history-row-${r.id}`} className="border-b border-border last:border-0">
                          <td className="px-4 py-2.5 font-mono">{r.slot_label}</td>
                          <td className="px-4 py-2.5"><span className="inline-flex rounded-md bg-primary/10 text-primary px-2 py-0.5 font-semibold">{r.class_name}</span></td>
                          <td className="px-4 py-2.5">{r.teacher_name}</td>
                          <td className="px-4 py-2.5">
                            {r.notified ? (
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                                  <CheckCircle2 className="h-4 w-4" /> Avvisato
                                </span>
                                {ch.includes("email") && (
                                  <span className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-xs"><Mail className="h-3 w-3" /> Email</span>
                                )}
                                {ch.includes("telegram") && (
                                  <span className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-xs"><Send className="h-3 w-3" /> Telegram</span>
                                )}
                                <span className="text-xs text-muted-foreground">{formatDateTimeIt(r.notified_at)}</span>
                              </div>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">
                                <XCircle className="h-4 w-4" /> Non avvisato
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
